// Minimal i18n on top of Intl: message catalogs with {param} and {n, plural, ...} syntax,
// locale-aware number/currency/date formatting, and a React hook that re-renders on change.
import { useSyncExternalStore } from 'react';
import { settingsStore } from '@/settings/settingsStore';
import { isCdr, type Asset, type AssetClass, type Locale } from '@/types';
import en from './messages/en';
import zhCN from './messages/zh-CN';

export type MessageKey = keyof typeof zhCN;

export const LOCALES: { id: Locale; label: string; tag: string; languageName: string }[] = [
  { id: 'zh-CN', label: '简体中文', tag: 'zh-CN', languageName: 'Simplified Chinese' },
  { id: 'en', label: 'English', tag: 'en-US', languageName: 'English' },
  { id: 'fr-CA', label: 'Français (Canada)', tag: 'fr-CA', languageName: 'Canadian French' },
];

// fr-CA has no strings yet and falls back to English (translation is scheduled for launch).
const CATALOGS: Record<Locale, Partial<Record<MessageKey, string>>> = { 'zh-CN': zhCN, en, 'fr-CA': {} };
export const AVAILABLE_LOCALES: Locale[] = ['zh-CN', 'en'];

export function detectLocale(): Locale {
  const langs = typeof navigator !== 'undefined' ? navigator.languages ?? [navigator.language] : [];
  for (const l of langs) {
    const low = (l ?? '').toLowerCase();
    if (low.startsWith('zh')) return 'zh-CN';
    if (low.startsWith('fr')) return 'fr-CA';
    if (low.startsWith('en')) return 'en';
  }
  return 'en';
}

export function currentLocale(): Locale {
  return settingsStore.get().locale ?? detectLocale();
}

export function localeInfo(locale: Locale = currentLocale()) {
  return LOCALES.find((l) => l.id === locale) ?? LOCALES[1];
}

export function intlTag(locale: Locale = currentLocale()): string {
  return localeInfo(locale).tag;
}

/** Resolves `{name}` params and `{n, plural, =0 {…} one {…} other {…}}` blocks (`#` = the formatted count). */
export function formatMessage(template: string, params: Record<string, string | number> = {}, tag: string = intlTag()): string {
  const withPlurals = template.replace(/\{(\w+),\s*plural,((?:\s*(?:=\d+|zero|one|two|few|many|other)\s*\{[^{}]*\})+)\s*\}/g, (_m, name: string, branches: string) => {
    const raw = params[name];
    const n = typeof raw === 'number' ? raw : Number(raw ?? 0);
    const cases: Record<string, string> = {};
    for (const b of branches.matchAll(/(=\d+|zero|one|two|few|many|other)\s*\{([^{}]*)\}/g)) cases[b[1]] = b[2];
    const chosen = cases[`=${n}`] ?? cases[new Intl.PluralRules(tag).select(n)] ?? cases.other ?? '';
    return chosen.replace(/#/g, new Intl.NumberFormat(tag).format(n));
  });
  return withPlurals.replace(/\{(\w+)\}/g, (m, name: string) => (params[name] == null ? m : String(params[name])));
}

export function t(key: MessageKey, params?: Record<string, string | number>): string {
  const locale = currentLocale();
  const raw = CATALOGS[locale][key] ?? CATALOGS.en[key] ?? zhCN[key] ?? key;
  return formatMessage(raw, params, intlTag(locale));
}

/** Subscribes the component to settings so it re-renders when the language changes. */
export function useT() {
  const settings = useSyncExternalStore(settingsStore.subscribe, settingsStore.get);
  const locale = settings.locale ?? detectLocale();
  return { t, locale, tag: intlTag(locale) };
}

export function assetClassLabel(c: AssetClass): string {
  return t(`class.${c}` as const);
}

/** Class label that knows the market: 加股 / 加拿大 ETF, and CDR for depositary receipts (long form for the LLM context). */
export function holdingLabel(a: Pick<Asset, 'assetClass' | 'market' | 'listingType'>, long = false): string {
  if (isCdr(a)) return t(long ? 'ctx.cdr' : 'class.cdr');
  if (a.assetClass === 'fund') return t(long ? 'ctx.fund' : a.market === 'CA' ? 'class.fundCA' : 'class.fund');
  if (a.market === 'CA' && a.assetClass === 'stock') return t('class.stockCA');
  if (a.market === 'CA' && a.assetClass === 'etf') return t('class.etfCA');
  return assetClassLabel(a.assetClass);
}

/** TradingView widget locale code for the current UI language. */
export function tradingViewLocale(locale: Locale = currentLocale()): string {
  return locale === 'zh-CN' ? 'zh_CN' : locale === 'fr-CA' ? 'fr' : 'en';
}

/** Localized label from a per-locale record such as catalog.json `field`. */
export function pickLocalized(record: Record<string, string> | string, locale: Locale = currentLocale()): string {
  if (typeof record === 'string') return record;
  return record[locale] ?? record.en ?? Object.values(record)[0] ?? '';
}
